/**
 * AddVersionModal Component
 *
 * Modal for adding a new version to an existing marketplace listing.
 * Allows selecting from deployments with valid versionTag attributes.
 */

import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Textarea } from '../../../components/ui/Textarea';
import { Alert } from '../../../components/ui/Alert';
import type { MarketplaceListing, MarketplaceVersionCreateDTO } from '../../../types/marketplace.types';
import type { DeploymentSanitized } from '../../../types/deployment.types';
import { deploymentService } from '../../../api/deployment.service';
import { useAddMarketplaceVersion } from '../hooks/usePublishedListings';
import { getErrorMessage } from '../../../lib/utils/errors';
import { formatDistanceToNow } from 'date-fns';

interface AddVersionModalProps {
  isOpen: boolean;
  onClose: () => void;
  listing: MarketplaceListing;
  tenantId: string;
}

/**
 * AddVersionModal component
 */
export const AddVersionModal: React.FC<AddVersionModalProps> = ({
  isOpen,
  onClose,
  listing,
  tenantId,
}) => {
  const [selectedDeploymentId, setSelectedDeploymentId] = useState<string>('');
  const [releaseNotes, setReleaseNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const addVersionMutation = useAddMarketplaceVersion();

  // Fetch deployments for the source application
  const {
    data: deployments = [],
    isLoading: deploymentsLoading,
    error: deploymentsError,
  } = useQuery({
    queryKey: ['deployments', tenantId, listing.sourceApplicationId],
    queryFn: () => deploymentService.listDeployments(tenantId, listing.sourceApplicationId),
    enabled: isOpen,
  });

  // Filter deployments that have a versionTag, are deployed, and not already published
  const validDeployments = useMemo(() => {
    const publishedVersions = new Set(listing.availableVersions?.map(v => v.version) || []);
    return deployments.filter((d: DeploymentSanitized) =>
      d.versionTag &&
      d.deploymentStatus === 'deployed' &&
      !publishedVersions.has(d.versionTag)
    );
  }, [deployments, listing.availableVersions]);

  // Get selected deployment details
  const selectedDeployment = useMemo(() => {
    return validDeployments.find((d: DeploymentSanitized) => d.deploymentId === selectedDeploymentId);
  }, [validDeployments, selectedDeploymentId]);

  // Get latest published version for display
  const latestPublishedVersion = listing.availableVersions?.[listing.availableVersions.length - 1];

  const handleClose = () => {
    if (!addVersionMutation.isPending) {
      setSelectedDeploymentId('');
      setReleaseNotes('');
      setError(null);
      onClose();
    }
  };

  const handleAddVersion = async () => {
    setError(null);

    if (!selectedDeploymentId) {
      setError('Please select a deployment to publish');
      return;
    }

    const data: MarketplaceVersionCreateDTO = {
      deploymentId: selectedDeploymentId,
      releaseNotes: releaseNotes.trim() || undefined,
    };

    try {
      await addVersionMutation.mutateAsync({ tenantId, listingId: listing.listingId, data });
      handleClose();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>Add New Version</Modal.Header>

      <Modal.Body>
        <div className="space-y-4">
          {/* Listing Info */}
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
              Listing: <span className="font-medium text-gray-900">{listing.name}</span>
            </p>
            <p className="text-xs text-gray-500">
              Current version: {latestPublishedVersion?.version || 'None'}
            </p>
          </div>

          {/* Deployment Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Deployment *
            </label>
            {deploymentsLoading ? (
              <div className="flex items-center text-sm text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mr-2"></div>
                Loading deployments...
              </div>
            ) : deploymentsError ? (
              <Alert variant="error" className="text-sm">
                Failed to load deployments: {getErrorMessage(deploymentsError)}
              </Alert>
            ) : validDeployments.length === 0 ? (
              <Alert variant="warning" className="text-sm">
                <p className="font-medium">No new versions available</p>
                <p className="mt-1">
                  All tagged deployments have already been published, or there are no deployments with a version tag.
                  Please deploy a new version with a version tag first.
                </p>
              </Alert>
            ) : (
              <>
                <select
                  value={selectedDeploymentId}
                  onChange={(e) => setSelectedDeploymentId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select a deployment...</option>
                  {validDeployments.map((deployment: DeploymentSanitized) => (
                    <option key={deployment.deploymentId} value={deployment.deploymentId}>
                      {deployment.versionTag} - v{deployment.version}
                      {deployment.isLatest ? ' (LATEST)' : ''}
                    </option>
                  ))}
                </select>
                {selectedDeployment && (
                  <div className="mt-2 p-3 bg-gray-50 rounded-md text-sm">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                        {selectedDeployment.versionTag}
                      </span>
                      <span className="text-gray-600">
                        Deployed {formatDistanceToNow(new Date(selectedDeployment.createdAt), { addSuffix: true })}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      This version will be added to the marketplace listing.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Release Notes */}
          <div>
            <Textarea
              label="Release Notes"
              value={releaseNotes}
              onChange={(e) => setReleaseNotes(e.target.value)}
              placeholder="What's new in this version..."
              rows={4}
            />
          </div>

          {/* Error */}
          {error && (
            <Alert variant="error">{error}</Alert>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={handleClose} disabled={addVersionMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleAddVersion}
            loading={addVersionMutation.isPending}
            disabled={addVersionMutation.isPending || validDeployments.length === 0 || !selectedDeploymentId}
          >
            Add Version
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default AddVersionModal;
